import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { getProducts } from '../services/api'
import { formatCurrency } from '../lib/formatters'
import { useSort } from '../hooks/useSort'
import SortHeader from '../components/ui/SortHeader'
import Pagination from '../components/ui/Pagination'
import EmptyState from '../components/ui/EmptyState'
import { useUserMode } from '../contexts/UserModeContext'

export default function Products() {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const { isAccountant } = useUserMode()

  useEffect(() => {
    getProducts({ take: 9999 })
      .then((res) => {
        const data = res.data
        setProducts(Array.isArray(data) ? data : data.products || [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const { paged, page, setPage, totalPages, total, sortKey, sortDir, toggleSort, pageSize } = useSort(products, {
    defaultSort: 'name', defaultDir: 'asc', pageSize: 25, storageKey: 'products',
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Products & Services</h1>
        <Link to="/products/new" className="btn-primary flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> New Item
        </Link>
      </div>

      <div className="card overflow-hidden !p-0">
        {loading ? (
          <div className="space-y-3 p-6">{[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : products.length === 0 ? (
          <EmptyState type="products" message="No products or services yet." actionLabel="Add your first item" actionTo="/products/new" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50/80 border-b">
                    <SortHeader label="Name" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Type" sortKey="type" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    {isAccountant && <SortHeader label="HSN / SAC" sortKey="hsnSacCode" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />}
                    <SortHeader label="Default Rate" sortKey="defaultRate" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" />
                    <SortHeader label="GST %" sortKey="gstRate" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <th className="px-4 py-3 font-medium">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((p) => (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/products/${p.id}/edit`} className="text-brand-600 hover:underline font-medium">{p.name}</Link>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${p.type === 'PRODUCT' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}`}>
                          {p.type}
                        </span>
                      </td>
                      {isAccountant && <td className="px-4 py-3 font-mono text-xs text-gray-500">{p.hsnSacCode || '—'}</td>}
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(p.defaultRate)}</td>
                      <td className="px-4 py-3 text-gray-600">{Number(p.gstRate)}%</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{p.unit || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  )
}
